import { useCallback } from 'react';
import { rage, useObservable } from '~/rage';
import {
	hexToRgb,
	isRgbPaintType,
	IVehicleBodyColor,
	VehiclePaintType,
} from '~/shared';
import { CategoryTypes } from '../components/CategoryTab';
import {
	setGroup,
	setIndexedColor,
	setSelectedColor,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';

export const useReset = () => {
	const dispatch = useLayoutDispatch();
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const sectionIndex = useLayoutSelector(
		(state) => state.colorPicker.sectionIndex,
	);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);

	const handleReset = useCallback(() => {
		if (activeCategory !== CategoryTypes.PaintingWorks) {
			return;
		}

		if (!ownedVehiclePaint?.bodyColor) return;

		const { bodyColor, interiorColor } = ownedVehiclePaint;

		const section = sectionIndex === 0 ? bodyColor.color : interiorColor;

		dispatch(setSelectedColor(hexToRgb(section as string)));

		let pearlColorIndex: number | null = null;
		if (sectionIndex === 0 && bodyColor.type === VehiclePaintType.Pearl) {
			const pearlMatch = pearlColors.find(
				(color) => color.index === bodyColor.pearlColor,
			);

			if (pearlMatch) {
				pearlColorIndex = pearlMatch.index;
			}
		}

		if (sectionIndex === 0) {
			rage.vehiclePaintShop.previewBodyColor(
				bodyColor.color,
				bodyColor.type,
				pearlColorIndex,
			);
		} else {
			rage.vehiclePaintShop.previewInteriorColor(interiorColor);
		}
	}, [activeCategory, ownedVehiclePaint, sectionIndex, pearlColors]);

	const resetBodyColor = useCallback(
		(bodyColor: IVehicleBodyColor) => {
			if (isRgbPaintType(bodyColor.type)) {
				const pearlColorIndex =
					bodyColor.type === VehiclePaintType.Pearl
						? pearlColors.find((color) => color.index === bodyColor.pearlColor)
								?.index ?? null
						: null;

				rage.vehiclePaintShop.previewBodyColor(
					bodyColor.color,
					bodyColor.type,
					pearlColorIndex,
				);
			} else {
				const indexedMatch = vehicleIndexedColors
					.filter((color) => color.type === bodyColor.type)
					.find((color) => color.index === bodyColor.color);

				if (indexedMatch) {
					dispatch(setIndexedColor(indexedMatch.index));
					dispatch(setSelectedColor({ r: 255, g: 255, b: 255 }));
				}

				rage.vehiclePaintShop.previewBodyColor(
					bodyColor.color,
					bodyColor.type,
					null,
				);
			}

			dispatch(
				setGroup(paintGroups.find((group) => group.type === bodyColor.type)),
			);
		},
		[pearlColors, vehicleIndexedColors, paintGroups],
	);

	const resetInteriorColor = useCallback((interiorColor: string) => {
		dispatch(setSelectedColor(hexToRgb(interiorColor)));
		rage.vehiclePaintShop.previewInteriorColor(interiorColor);
	}, []);

	const handleResetBodyColor = useCallback(() => {
		if (!ownedVehiclePaint?.bodyColor) return;

		const { bodyColor, interiorColor } = ownedVehiclePaint;
		resetBodyColor(bodyColor);

		if (interiorColor) {
			resetInteriorColor(interiorColor);
		}
	}, [ownedVehiclePaint, resetBodyColor, resetInteriorColor]);

	const handleResetInteriorColor = useCallback(() => {
		if (!ownedVehiclePaint?.interiorColor) return;

		const { bodyColor, interiorColor } = ownedVehiclePaint;
		resetInteriorColor(interiorColor);

		if (isRgbPaintType(bodyColor.type)) {
			dispatch(setSelectedColor(hexToRgb(bodyColor.color as string)));
			resetBodyColor(bodyColor);
		}
	}, [ownedVehiclePaint, resetBodyColor, resetInteriorColor]);

	return { handleReset, handleResetBodyColor, handleResetInteriorColor };
};
